import { useEffect, useId, useState, useSyncExternalStore } from "react";

const subscribe = () => () => {};

type ShareButtonsProps = {
  url: string;
  title?: string;
};

export function ShareButtons({ url, title }: ShareButtonsProps) {
  const headingId = useId();
  const canShare = useSyncExternalStore(
    subscribe,
    () => typeof navigator !== "undefined" && typeof navigator.share === "function",
    () => false,
  );
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (!status) return;
    const timeout = window.setTimeout(() => setStatus(""), 2500);
    return () => window.clearTimeout(timeout);
  }, [status]);

  const links = [
    { label: "WhatsApp", href: "https://wa.me/?text=" + encodeURIComponent((title ? title + " " : "") + url) },
    { label: "Facebook", href: "https://www.facebook.com/sharer/sharer.php?u=" + encodeURIComponent(url) },
    { label: "LinkedIn", href: "https://www.linkedin.com/sharing/share-offsite/?url=" + encodeURIComponent(url) },
    { label: "X", href: "https://x.com/intent/post?url=" + encodeURIComponent(url) + "&text=" + encodeURIComponent(title || "") },
  ];
  const buttonClass = "inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-quatt-dark hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-quatt-orange";

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(url);
      setStatus("Copied");
    } catch {
      setStatus("Could not copy the link.");
    }
  }

  async function share() {
    try {
      await navigator.share({ url, title });
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") return;
      setStatus("Could not share the link.");
    }
  }

  return (
    <div role="group" aria-labelledby={headingId} className="not-prose my-8 rounded-xl border border-gray-200 bg-quatt-warm p-4">
      <p id={headingId} className="mb-3 text-sm font-semibold text-quatt-dark">Share this page</p>
      <div className="flex flex-wrap gap-2">
        {links.map(({ label, href }) => (
          <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={`Share on ${label} (opens in a new tab)`} className={buttonClass}>
            {label}
          </a>
        ))}
        <button type="button" onClick={copyLink} aria-label="Copy link" className={buttonClass}>
          Copy link
        </button>
        {canShare && (
          <button type="button" onClick={share} aria-label="Share using your device" className={buttonClass}>
            Share…
          </button>
        )}
      </div>
      <p role="status" aria-live="polite" aria-atomic="true" className="mt-2 min-h-5 text-sm text-gray-500">{status}</p>
    </div>
  );
}
